import { Check, Heart } from "lucide-react"
import { useTranslation } from "react-i18next"

import { Badge } from "@/components/ui/badge"
import { badgeVariants } from "@/components/ui/badge-variants"
import { Link, useTransient } from "@/lib/page-rendering"
import { SearchShortlistMembership } from "@/types/serializers"

// The shortlists that already hold the item a show page presents, each
// linking into its workspace. The one the item was just added to, named by
// the visit's transient data, is marked: that mark is the confirmation of
// the add, so it sits beside the action that performed it.
export function ShortlistMembership({
  shortlists,
}: {
  shortlists: SearchShortlistMembership[]
}) {
  const { t } = useTranslation("shortlists", { keyPrefix: "membership" })
  const { shortlisted } = useTransient<{ shortlisted?: string }>()

  if (shortlists.length === 0) return null

  return (
    <div
      data-testid="shortlist-membership"
      className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm"
    >
      <span className="text-muted-foreground">{t("title")}</span>
      <ul aria-label={t("title")} className="flex flex-wrap items-center gap-2">
        {shortlists.map(({ id, name }) => {
          const justAdded = id === shortlisted

          return (
            <li
              key={id}
              data-testid="shortlist-membership-item"
              className="flex flex-wrap items-center gap-0.5"
            >
              <Link
                href={`/search/shortlists/${id}`}
                aria-label={t("view_shortlist", { name })}
                className={badgeVariants({
                  variant: "secondary",
                  className: "py-1",
                })}
              >
                <Heart aria-hidden />
                {name}
              </Link>
              {justAdded && (
                // The space keeps the mark a separate word in the item's
                // text; flex layout renders no gap for it.
                <>
                  {" "}
                  <Badge
                    variant="active"
                    data-testid="shortlist-membership-just-added"
                  >
                    <Check aria-hidden />
                    {t("just_added")}
                  </Badge>
                </>
              )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}
